/**
 * 路径 2-A — Storage Bridge 接 prod 主路径
 * stage-storage-v2.ts — "v2 路径" 适配层
 *
 * Background: §37 passive library vendor 完成后，DocumentStore 仍未接 prod 调用链。
 * 本文件是"v2 路径" — 提供跟 lib/utils/stage-storage.ts 完全一致的 4 个 API
 * (saveStageData / loadStageData / deleteStageData / listStages + saveStageMetaOnly),
 * 内部走 DocumentStore (BrowserDocumentStore from @openmaic/storage)。
 *
 * 关键设计原则:
 * 1. **API 完全兼容** — 调用方 (lib/store/stage.ts / app/page.tsx 等) 只需改 import path,
 *    函数签名一字不差。
 * 2. **双写 (dual-write)** — save 时同时写 DocumentStore + Dexie, 任何一边失败不阻断另一边。
 *    DocumentStore 提供 migrate-on-read, Dexie 保留 dongniai 业务字段 (outlineId / progress 等)。
 * 3. **load 走 DocumentStore 优先** — 如果 DocumentStore 有该 stageId, 走 v2 (migrate-on-read);
 *    如果没有, 自动 fallback 到 Dexie (老数据不会丢)。
 * 4. **listStages 走 Dexie** — 因为 list 只需要 StageListItem (不含 scenes 内容),
 *    Dexie 路径走 db.stages.orderBy('updatedAt').reverse() 更便宜;
 *    DocumentStore.listDocuments() 也能用, 但 Dexie 是 prod 已有数据源, 不需要迁移。
 *
 * 未来 (HTTP backend 上线后): 把 getDongniaiDocumentStore() 换成 HTTP backend 即可,
 * 整个切换无需改 prod 调用方。
 */
import type { Stage, Scene, AppScene } from '@/lib/types/stage';
import type { VideoManifest } from '@openmaic/dsl';
import type { ChatSession } from '@/lib/types/chat';
import type { Slide } from '@/lib/types/slides';
import {
  saveStageData as saveStageDataV1,
  saveStageMetaOnly as saveStageMetaOnlyV1,
  saveStageMetaOnlyBatch as saveStageMetaOnlyBatchV1,
  loadStageData as loadStageDataV1,
  deleteStageData as deleteStageDataV1,
  listStages as listStagesV1,
  renameStage as renameStageV1,
  getFirstSlideByStages as getFirstSlideByStagesV1,
  type StageStoreData,
  type StageListItem,
} from '@/lib/utils/stage-storage';
import { saveChatSessions, loadChatSessions, deleteChatSessions } from '@/lib/utils/chat-storage';
import { clearPlaybackState } from '@/lib/utils/playback-storage';
import { clearAllForScene } from '@/lib/quiz/persistence';
import {
  saveClassroomViaMaic,
  putSceneViaMaic,
  loadClassroomViaMaic,
  deleteClassroomViaMaic,
  type DongniaiStage,
} from '@/lib/storage-bridge';
import { createLogger } from '@/lib/logger';

const log = createLogger('StageStorageV2');

/**
 * 转换 prod Stage + 业务字段 → DongniaiStage (Bridge 类型).
 * 注意: currentSceneId 是 StageRecord 字段, 不是 DSL Stage 字段 — 单独保留.
 */
function toDongniaiStage(stage: Stage, currentSceneId?: string | null): DongniaiStage {
  // DongniaiStage = Stage (DSL) + {outlineId, lessonIndex, progress, currentSceneId}
  // prod Stage 来自 useStageStore, 已经包含 DSL 字段; 业务字段 (outlineId 等)
  // 来自 Dexie StageRecord (saving 时一并写入 doc stage).
  return stage as DongniaiStage;
}

/**
 * AppScene extends Scene with 'interactive' | 'pbl' kinds (see lib/types/stage.ts
 * AppSceneContent = DslSceneContent | InteractiveContent | PBLContent). The Bridge
 * surface accepts AppScene (prod callers hold that); save-classroom-bridge.ts casts
 * to Scene at the storage boundary.
 */
type AsScene = (s: AppScene) => Scene;
const asScene = ((s: AppScene) => s) as unknown as AsScene;
const asScenes = ((arr: AppScene[]) => arr) as unknown as (arr: AppScene[]) => Scene[];

/**
 * Save stage data — DocumentStore 优先 + Dexie 双写.
 * 任一失败不阻断另一边 (独立 try/catch).
 */
export async function saveStageData(stageId: string, data: StageStoreData): Promise<void> {
  // 1. DocumentStore 写入 (v2 — 提供 migrate-on-read + validation gate)
  try {
    const dongniaiStage = toDongniaiStage(data.stage, data.currentSceneId);
    await saveClassroomViaMaic({
      stage: dongniaiStage,
      scenes: asScenes(data.scenes),
      outline: undefined, // prod 当前没有 outline 字段 (跟官方 ManifestStage 一致)
    });
    log.info(`[v2] Saved stage via DocumentStore: ${stageId}`);
  } catch (v2Err) {
    log.warn(`[v2] DocumentStore save failed, falling back to Dexie-only:`, v2Err);
  }

  // 2. Dexie 双写 (保持 prod 现有 IndexedDB 路径 100% 工作, 老数据不丢)
  await saveStageDataV1(stageId, data);
}

/**
 * Save stage meta only — 仅 Dexie 路径 (listStages 走 Dexie, 不需要 DocumentStore).
 */
export async function saveStageMetaOnly(meta: StageListItem): Promise<void> {
  return saveStageMetaOnlyV1(meta);
}

/**
 * 【v6.48.2 batch 优化 2026-07-11】Save stage meta only 批量版本
 *   老板反馈"打开慢"，原 100 次串行 saveStageMetaOnly + 100 条 console.log 改为 1 次 bulkPut
 *   收益：IndexedDB 卡 200-500ms → <50ms，console 100 条 → 1 条
 *   修因：v6.46 9ac05689 引入 saveStageMetaOnly 串行循环让首页首次加载卡 500ms
 *   这里只 Dexie 路径（listStages 走 Dexie, 不需要 DocumentStore，跟单条 saveStageMetaOnly 一致）
 */
export async function saveStageMetaOnlyBatch(metaList: StageListItem[]): Promise<void> {
  if (metaList.length === 0) return;
  // 直接调 v1 batch 函数（Dexie bulkPut 一次事务）
  return saveStageMetaOnlyBatchV1(metaList);
}

/**
 * Load stage data — DocumentStore 优先, Dexie fallback, server fallback.
 *
 * 三级 fallback 链路（v6.46 修复 2026-07-11）：
 *   1. DocumentStore（v2）— 字母 ID lesson 的主路径，migrate-on-read + validation
 *   2. Dexie（v1 老路径）— 老用户 IndexedDB stage meta only（Dexie 兜底）
 *   3. Server fallback (`/api/classroom?id=${stageId}`) — Dexie scenes 缺失时拉服务端
 *
 * 为什么需要 server fallback：
 *   - d3f0de46 (7/3 老稳态) 时 lib/store/stage.ts 老路径永远同时返回 stage+scenes
 *   - §38 commit a777a740 (7/10) 改成走 v2 bridge，DocumentStore 没数据时 fallback Dexie
 *   - 但 Dexie 在 saveStageMetaOnly 后只有 stage 表有 meta，scenes 表是空的
 *   - 结果：v2 fallback 链上虽然 fallback 到了 Dexie，但 Dexie 的 scenes=[]，
 *     后续 page.tsx 的 `if (!stage)` 判断又被 stage-truthy 挡住 → 永远不 fetch server
 *   - 加 server fallback 让 v2 桥直接调 /api/classroom，scenes 永远能 populate
 *
 * 不破坏现有（参考官方 OpenMAIC 无 server 依赖的 offline-first 哲学，但 dongniai 有跨设备同步需求）：
 *   - DocumentStore 有 → 走原路径（不联网）
 *   - Dexie 完整 → 走 v1 老路径（IndexedDB-only, 不联网）
 *   - 都缺 → server 兜底（联网，10s 超时）
 *   - 都失败 → return null（页面走老"课堂数据不存在"错误）
 */
export async function loadStageData(stageId: string): Promise<StageStoreData | null> {
  // 1. DocumentStore 优先
  try {
    const v2 = await loadClassroomViaMaic(stageId);
    if (v2) {
      const chats = await loadChatSessions(stageId);
      log.info(`[v2] Loaded stage via DocumentStore: ${stageId}, scenes: ${v2.scenes.length}`);
      return {
        stage: v2.stage,
        scenes: v2.scenes,
        currentSceneId: v2.scenes[0]?.id ?? null,
        chats,
      };
    }
  } catch (v2Err) {
    log.warn(`[v2] DocumentStore load failed, falling back to Dexie:`, v2Err);
  }

  // 2. Dexie fallback (老数据 + saveStageMetaOnly 写入的 meta-only 路径)
  const v1Data = await loadStageDataV1(stageId);
  if (v1Data && v1Data.scenes.length > 0) {
    // Dexie 完整（有 scenes）— 直接用 v1
    return v1Data;
  }

  // 2.5 Dexie 有 stage 但没 scenes（saveStageMetaOnly 产物），先看看 server 能不能拿全量
  if (v1Data && v1Data.stage) {
    const serverData = await fetchStageDataFromServer(stageId);
    if (serverData) {
      // 同步写回 IndexedDB（DocumentStore 优先 + Dexie 兜底，让下次进直接命中 v1）
      try {
        await saveStageDataV1(stageId, { ...v1Data, ...serverData, chats: [] });
        log.info(`[v2] Persisted server-restored scenes to Dexie: ${stageId}, scenes: ${serverData.scenes.length}`);
      } catch (persistErr) {
        log.warn(`[v2] Failed to persist server-restored scenes (in-memory still used):`, persistErr);
      }
      return {
        stage: serverData.stage,
        scenes: serverData.scenes,
        currentSceneId: serverData.scenes[0]?.id ?? null,
        chats: [],
      };
    }
    // server 也拿不到 → fallback 一下, return v1Data(虽然 scenes 是空)，让 page.tsx 显示原"课堂数据不存在"
    return v1Data;
  }

  // 3. Dexie 都没有，直接 server fallback
  if (!v1Data) {
    const serverData = await fetchStageDataFromServer(stageId);
    if (serverData) {
      return {
        stage: serverData.stage,
        scenes: serverData.scenes,
        currentSceneId: serverData.scenes[0]?.id ?? null,
        chats: [],
      };
    }
  }

  return null;
}

/**
 * 【v6.46 helper】调 /api/classroom?id=... 拉全量 stage+scenes
 * 返回 null 表示 server 也找不到（curl 不到 + 4xx/5xx）
 */
async function fetchStageDataFromServer(stageId: string): Promise<{ stage: Stage; scenes: Scene[] } | null> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    const res = await fetch(`/api/classroom?id=${encodeURIComponent(stageId)}`, {
      signal: controller.signal,
      credentials: 'include',
    });
    clearTimeout(timeout);
    if (!res.ok) {
      log.info(`[v2] Server fallback returned non-OK for ${stageId}: ${res.status}`);
      return null;
    }
    const json = await res.json();
    if (!json?.success || !json?.classroom?.stage || !Array.isArray(json?.classroom?.scenes)) {
      log.info(`[v2] Server fallback returned invalid payload for ${stageId}`);
      return null;
    }
    return json.classroom as { stage: Stage; scenes: Scene[] };
  } catch (fetchErr) {
    log.warn(`[v2] Server fallback failed for ${stageId}:`, fetchErr);
    return null;
  }
}

/**
 * Delete stage data — DocumentStore + Dexie 双删.
 */
export async function deleteStageData(stageId: string): Promise<void> {
  // 1. DocumentStore 删除
  try {
    await deleteClassroomViaMaic(stageId);
    log.info(`[v2] Deleted stage via DocumentStore: ${stageId}`);
  } catch (v2Err) {
    log.warn(`[v2] DocumentStore delete failed:`, v2Err);
  }

  // 2. Dexie 删除 (保留 quiz/playback 清理逻辑, 跟 v1 完全一致)
  return deleteStageDataV1(stageId);
}

/**
 * List all stages — Dexie 路径 (不需要 DocumentStore, list 便宜).
 */
export async function listStages(): Promise<StageListItem[]> {
  return listStagesV1();
}

/**
 * Rename stage — DocumentStore + Dexie 双写.
 */
export async function renameStage(stageId: string, newName: string): Promise<void> {
  // DocumentStore path — load + modify + save (migrate-on-read 在 load 时跑)
  try {
    const v2 = await loadClassroomViaMaic(stageId);
    if (v2) {
      await saveClassroomViaMaic({
        stage: { ...v2.stage, name: newName, updatedAt: Date.now() },
        scenes: v2.scenes,
        outline: v2.outline,
      });
    }
  } catch (v2Err) {
    log.warn(`[v2] renameStage via DocumentStore failed:`, v2Err);
  }
  // Dexie 写主路径 (保持一致)
  return renameStageV1(stageId, newName);
}

/**
 * Get first slide by stages — 跟 v1 一致 (Dexie path).
 */
export async function getFirstSlideByStages(stageIds: string[]): Promise<Record<string, Slide>> {
  return getFirstSlideByStagesV1(stageIds);
}

/**
 * 增量写单个 scene — DocumentStore putScene (per-scene incremental write).
 * prod 暂未使用 (现有 saveStageData 是整块覆盖), 但 export 供未来 patch 用.
 */
export async function saveSingleScene(stageId: string, scene: AppScene): Promise<void> {
  try {
    await putSceneViaMaic({ stageId, scene });
  } catch (v2Err) {
    log.warn(`[v2] putScene failed (DocumentStore):`, v2Err);
    // 单 scene 增量写入失败不抛 — 调用方自己用 saveStageData 整块覆盖兜底
  }
}

// 透出给 prod 调用方的所有类型 + 函数 — 完全兼容 stage-storage.ts 的导出形状
export type { StageStoreData, StageListItem };

// VideoManifest 类型透出 — 调用方可能用 (e.g. saveStageData 内部)
export type { VideoManifest };